//
// Copyright (c) 2025 Comcast
//

// TextColorPopover: Popover to apply the custom 'textColor' mark (see extension) with a 12-color palette.
import * as React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'

// Hooks
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'
import { useMenuNavigation } from '@tiptap-ui/hooks/use-menu-navigation'

import { HighlighterIcon } from '@tiptap-ui/components/tiptap-icons/highlighter-icon'

// Primitives
import { Button } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Popover, PopoverTrigger, PopoverContent } from '@tiptap-ui/components/tiptap-ui-primitive/popover'
import { Card, CardBody } from '@tiptap-ui/components/tiptap-ui-primitive/card'

export const ATLASSIAN_TEXT_COLOR_PALETTE = [
  { label: 'Black', value: '#172B4D' },
  { label: 'Dark grey', value: '#505F79' },
  { label: 'Light grey', value: '#C1C7D0' },
  { label: 'Blue', value: '#0747A6' },
  { label: 'Light blue', value: '#4C9AFF' },
  { label: 'Green', value: '#00875A' },
  { label: 'Pale green', value: '#57D9A3' },
  { label: 'Red', value: '#DE350B' },
  { label: 'Dark orange', value: '#FF8B00' },
  { label: 'Light orange', value: '#FFAB00' },
  { label: 'Purple', value: '#403294' },
  { label: 'Peach', value: '#FFBDAD' },
]

const DEFAULT_TEXT_COLORS = ATLASSIAN_TEXT_COLOR_PALETTE

const CardContent = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  flex-direction: column;
  padding: 4px;
  gap: 8px;
`

const ColorsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

// &&&& changes specificity to override generic icon coloring
const StyledHighlighterIcon = styled(HighlighterIcon)`
  ${props => props.$color && css`
    transition: color .15s ease;
    &&&& {
      color: ${props => props.$color};
    }
  `}
`

// TextColorPopoverButton: shows highlighter icon tinted with current text color (if any).
export const TextColorPopoverButton = React.forwardRef(({ className, children, currentColor, ...props }, ref) => (
  <Button
    type='button'
    className={className}
    data-style='ghost'
    data-appearance='default'
    role='button'
    tabIndex={-1}
    aria-label='Text color'
    tooltip='Text color'
    ref={ref}
    {...props}
  >
    <StyledHighlighterIcon className='tiptap-button-icon' $color={currentColor || undefined} />
  </Button>
))
TextColorPopoverButton.displayName = 'TextColorPopoverButton'

// Internal swatch button for applying a color
const StyledColorButton = styled(Button)`
  padding: 0;
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledColorSwatch = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 1px;
  outline: ${props => props.$isHighlighted ? '2px solid ' + colors.borderPrimaryDefault : 'none'};
  background-color: ${props => props.$color};
  transition: outline 0.12s ease;
  &:hover {
    outline: 2px solid ${colors.borderPrimaryDefault};
  }
`

function TextColorSwatchButton ({ editor, color, label, isHighlighted, tabIndex }) {
  const handleClick = React.useCallback(() => {
    if (!editor) {
      return
    }
    editor.chain().focus().setMark('textColor', { color }).run()
  }, [editor, color])

  return (
    <StyledColorButton
      type='button'
      role='menuitem'
      tabIndex={tabIndex}
      tooltip={label}
      data-style='ghost'
      data-active-state={isHighlighted ? 'on' : 'off'}
      data-highlighted={isHighlighted}
      onClick={handleClick}
    >
      <StyledColorSwatch $color={color} $isHighlighted={isHighlighted} />
    </StyledColorButton>
  )
}

export function TextColorPopoverContent ({ editor, colors = DEFAULT_TEXT_COLORS }) {
  const containerRef = React.useRef(null)
  // Current active color
  const activeAttrColor = editor?.getAttributes('textColor')?.color?.toLowerCase()

  const menuItems = React.useMemo(() => [...colors, { label: 'Reset color', value: 'none' }], [colors])

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: 'both',
    onSelect: (item) => {
      if (!containerRef.current) {
        return false
      }
      const highlightedElement = containerRef.current.querySelector('[data-highlighted="true"]')
      if (highlightedElement) {
        highlightedElement.click()
      }
      if (item.value === 'none') {
        editor?.chain().focus().unsetMark('textColor').run()
      }
    },
    autoSelectFirstItem: false,
  })

  return (
    <Card ref={containerRef} tabIndex={0}>
      <CardBody>
        <CardContent>
          <ColorsGrid>
            {colors.map((c, index) => {
              const isActiveColor = activeAttrColor && c.value.toLowerCase() === activeAttrColor
              return (
                <TextColorSwatchButton
                  key={c.value}
                  editor={editor}
                  color={c.value}
                  label={c.label}
                  tabIndex={index === selectedIndex ? 0 : -1}
                  isHighlighted={selectedIndex === index || isActiveColor}
                />
              )
            })}
          </ColorsGrid>
          <IconLink
            onClick={() => editor?.chain().focus().unsetMark('textColor').run()}
            iconName='icon-refetch'
            text='Reset'
          />
        </CardContent>
      </CardBody>
    </Card>
  )
}

export function TextColorPopover ({ editor: providedEditor, colors = DEFAULT_TEXT_COLORS, hideWhenUnavailable = false, ...props }) {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = React.useState(false)
  const [currentColor, setCurrentColor] = React.useState('')

  // Effect pattern to subscribe to selection + transaction, minimal updates.
  React.useEffect(() => {
    if (!editor) {
      return
    }
    let prev = ''
    const update = () => {
      const next = editor.getAttributes('textColor')?.color || ''
      if (next !== prev) {
        prev = next
        setCurrentColor(next)
      }
    }
    editor.on('selectionUpdate', update)
    editor.on('transaction', update)
    update()
    return () => {
      editor.off('selectionUpdate', update)
      editor.off('transaction', update)
    }
  }, [editor])

  const isVisible = !!editor
  const canApply = !!editor
  const isActive = editor?.isActive('textColor')
  const label = 'Text color'
  if (!isVisible) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <TextColorPopoverButton
          disabled={!canApply}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={!canApply}
          aria-pressed={isActive}
          aria-label={label}
          tooltip={label}
          currentColor={currentColor}
          {...props}
        />
      </PopoverTrigger>
      <PopoverContent aria-label='Text colors'>
        <TextColorPopoverContent editor={editor} colors={colors} />
      </PopoverContent>
    </Popover>
  )
}

export default TextColorPopover

TextColorPopoverButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  currentColor: PropTypes.string,
}

TextColorSwatchButton.propTypes = {
  editor: PropTypes.object,
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
  tabIndex: PropTypes.number,
}

TextColorPopoverContent.propTypes = {
  editor: PropTypes.object,
  colors: PropTypes.array,
}

TextColorPopover.propTypes = {
  editor: PropTypes.object,
  colors: PropTypes.array,
  hideWhenUnavailable: PropTypes.bool,
}
